/**
 * SearchSidebar — Left sidebar for Search workspace
 * 
 * Contains:
 * - Search input
 * - Quick topic chips from preferences
 * - Filters (journal, study design, date range)
 * - Recent searches
 * - "Use my digest topics" shortcut
 */
import React from 'react';
import { Input } from '../../ui/input';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { ScrollArea } from '../../ui/scroll-area';
import { Separator } from '../../ui/separator';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Label } from '../../ui/label';
import { preferencesAPI } from '../../../api';
import { LibraryStatusCard } from '../LibraryStatusCard';
import {
  Search,
  Sparkles,
  Clock,
  Filter,
  X,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';

// Study design options
const STUDY_DESIGNS = [
  { value: 'all', label: 'All Study Types' },
  { value: 'RCT', label: 'Randomized Controlled Trial' },
  { value: 'Meta-Analysis', label: 'Meta-Analysis' },
  { value: 'Systematic Review', label: 'Systematic Review' },
  { value: 'Cohort Study', label: 'Cohort Study' },
  { value: 'Case-Control', label: 'Case-Control Study' },
  { value: 'Clinical Trial', label: 'Clinical Trial' },
  { value: 'Review', label: 'Review' },
];

// Date range options
const DATE_RANGES = [
  { value: '7', label: 'Past week' },
  { value: '30', label: 'Past month' },
  { value: '90', label: 'Past 3 months' },
  { value: '365', label: 'Past year' },
  { value: '1825', label: 'Past 5 years' },
  { value: 'all', label: 'All time' },
];

interface SearchFilters {
  dateRange: string;
  studyDesign: string;
  journal: string;
}

interface SearchSidebarProps {
  query: string;
  onQueryChange: (query: string) => void;
  onSearch: () => void;
  filters: SearchFilters;
  onFiltersChange: (filters: SearchFilters) => void;
  recentSearches: string[];
  onRecentSearchClick: (query: string) => void;
  onClearRecentSearches: () => void;
  isSearching: boolean;
  /** `desktop` hides below md; `drawer` always visible (for mobile Sheet). */
  variant?: 'desktop' | 'drawer';
}

export const SearchSidebar: React.FC<SearchSidebarProps> = ({
  query,
  onQueryChange,
  onSearch,
  filters,
  onFiltersChange,
  recentSearches,
  onRecentSearchClick,
  onClearRecentSearches,
  isSearching,
  variant = 'desktop',
}) => {
  const [preferences, setPreferences] = React.useState<any>(null);
  const [showFilters, setShowFilters] = React.useState(false);
  const [loadingPrefs, setLoadingPrefs] = React.useState(true);

  // Load user preferences for topic chips
  React.useEffect(() => {
    const loadPreferences = async () => {
      try {
        const prefs = await preferencesAPI.getMyPreferences();
        setPreferences(prefs);
      } catch (error) {
        console.error('Failed to load preferences:', error);
      } finally {
        setLoadingPrefs(false);
      }
    };
    loadPreferences();
  }, []);

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      onSearch();
    }
  };

  const handleUseDigestTopics = () => {
    if (preferences?.custom_keywords?.length > 0) {
      onQueryChange(preferences.custom_keywords.slice(0, 3).join(' OR '));
    }
  };

  // Get topic chips from preferences
  const topicChips = React.useMemo(() => {
    const chips: string[] = [];
    if (preferences?.specialty_name) {
      chips.push(preferences.specialty_name);
    }
    if (preferences?.subspecialty_name) {
      chips.push(preferences.subspecialty_name);
    }
    if (preferences?.custom_keywords) {
      chips.push(...preferences.custom_keywords.slice(0, 5));
    }
    return chips;
  }, [preferences]);

  return (
    <aside 
      className={
        variant === 'drawer'
          ? 'w-full border-0 bg-[hsl(var(--card))] flex flex-col h-full'
          : 'hidden md:flex w-[280px] lg:w-[300px] border-r border-[hsl(var(--border))] bg-[hsl(var(--card))] flex-col h-full'
      }
      data-testid="search-sidebar"
    >
      <ScrollArea className="flex-1">
        <div className="p-4 space-y-4">
          {/* Library Status Card */}
          <div>
            <Label className="text-xs font-medium text-[hsl(var(--muted-foreground))] uppercase tracking-wide mb-2 block">
              Your LitHub
            </Label>
            <LibraryStatusCard variant="compact" />
          </div>

          <Separator />

          {/* Search Input */}
          <div>
            <Label className="text-xs font-medium text-[hsl(var(--muted-foreground))] uppercase tracking-wide mb-2 block">
              Search PubMed
            </Label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[hsl(var(--muted-foreground))]" />
              <Input
                placeholder="Enter keywords..."
                value={query}
                onChange={(e) => onQueryChange(e.target.value)}
                onKeyPress={handleKeyPress}
                className="pl-10 pr-10 h-11"
                data-testid="search-sidebar-input"
              />
              {query && (
                <button
                  onClick={() => onQueryChange('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
            <Button 
              onClick={onSearch} 
              className="w-full mt-2" 
              disabled={!query.trim() || isSearching}
              data-testid="search-sidebar-submit"
            >
              {isSearching ? 'Searching...' : 'Search'}
            </Button>
          </div>

          <Separator />

          {/* Quick Topic Chips */}
          {topicChips.length > 0 && (
            <div>
              <Label className="text-xs font-medium text-[hsl(var(--muted-foreground))] uppercase tracking-wide mb-2 block">
                Quick Topics
              </Label>
              <div className="flex flex-wrap gap-1.5">
                {topicChips.map((topic, i) => (
                  <Badge
                    key={i}
                    variant="secondary"
                    className="cursor-pointer hover:bg-[hsl(var(--primary))]/10 hover:text-[hsl(var(--primary))] transition-colors text-xs"
                    onClick={() => onQueryChange(topic)}
                    data-testid={`topic-chip-${i}`}
                  >
                    {topic}
                  </Badge>
                ))}
              </div>
              {preferences?.custom_keywords?.length > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="mt-2 w-full text-xs gap-1.5"
                  onClick={handleUseDigestTopics}
                  data-testid="use-digest-topics-btn"
                >
                  <Sparkles className="w-3.5 h-3.5" />
                  Use my digest topics
                </Button>
              )}
            </div>
          )}

          <Separator />

          {/* Filters Section */}
          <div>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center justify-between w-full text-xs font-medium text-[hsl(var(--muted-foreground))] uppercase tracking-wide mb-2"
            >
              <span className="flex items-center gap-1.5">
                <Filter className="w-3.5 h-3.5" />
                Filters
              </span>
              {showFilters ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </button>
            
            {showFilters && (
              <div className="space-y-3">
                {/* Date Range */}
                <div>
                  <Label className="text-xs text-[hsl(var(--muted-foreground))] mb-1.5 block">Date Range</Label>
                  <Select 
                    value={filters.dateRange} 
                    onValueChange={(v) => onFiltersChange({ ...filters, dateRange: v })}
                  >
                    <SelectTrigger className="h-9 text-sm" data-testid="filter-date-range">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {DATE_RANGES.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Study Design */}
                <div>
                  <Label className="text-xs text-[hsl(var(--muted-foreground))] mb-1.5 block">Study Design</Label>
                  <Select 
                    value={filters.studyDesign} 
                    onValueChange={(v) => onFiltersChange({ ...filters, studyDesign: v })}
                  >
                    <SelectTrigger className="h-9 text-sm" data-testid="filter-study-design">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {STUDY_DESIGNS.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Journal */}
                <div>
                  <Label className="text-xs text-[hsl(var(--muted-foreground))] mb-1.5 block">Journal</Label>
                  <Input
                    placeholder="e.g., NEJM, JAMA, Lancet"
                    value={filters.journal}
                    onChange={(e) => onFiltersChange({ ...filters, journal: e.target.value })}
                    className="h-9 text-sm"
                    data-testid="filter-journal"
                  />
                </div>
              </div>
            )}
          </div>

          <Separator />

          {/* Recent Searches */}
          {recentSearches.length > 0 && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-xs font-medium text-[hsl(var(--muted-foreground))] uppercase tracking-wide">
                  Recent Searches
                </Label>
                <button
                  onClick={onClearRecentSearches}
                  className="text-xs text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]"
                >
                  Clear
                </button>
              </div>
              <div className="space-y-1">
                {recentSearches.slice(0, 5).map((search, i) => (
                  <button
                    key={i}
                    onClick={() => onRecentSearchClick(search)}
                    className="flex items-center gap-2 w-full text-left text-sm text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] py-1.5 px-2 rounded-md hover:bg-[hsl(var(--muted))] transition-colors"
                    data-testid={`recent-search-${i}`}
                  >
                    <Clock className="w-3.5 h-3.5 flex-shrink-0" />
                    <span className="truncate">{search}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </ScrollArea>
    </aside>
  );
};

export default SearchSidebar;
